import React, { useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { ShoppingCart, Ruler, Truck, Percent, Calculator, ShieldCheck, Check } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import { categoryOf, CATEGORY_LABEL } from './materialUtils';
import { fmt } from '../../../lib/projectVisuals';

const COLORS = { betong: '#9C978F', stal: '#5E6670', tra: '#C89B63', murning: '#B5654A', isolering: '#E2C25A', gips: '#E9E6E0', tak: '#3E3A37', fonster: '#6E8C9C', install: '#C7703B', ytskikt: '#B9A58E', ovrigt: '#A8A29A' };

const Stack = ({ count, color }) => {
  const side = Math.ceil(Math.sqrt(count / 2));
  const boxes = useMemo(() => Array.from({ length: count }, (_, i) => {
    const layer = Math.floor(i / (side * side));
    const r = i % (side * side);
    return [(r % side) - side / 2 + 0.5, layer * 0.62 + 0.31, Math.floor(r / side) - side / 2 + 0.5];
  }), [count, side]);
  return (
    <group>
      <mesh position={[0, -0.06, 0]} receiveShadow><boxGeometry args={[side + 0.5, 0.12, side + 0.5]} /><meshStandardMaterial color="#B08A5A" /></mesh>
      {boxes.map((p, i) => (
        <mesh key={i} position={p} castShadow><boxGeometry args={[0.92, 0.58, 0.92]} /><meshStandardMaterial color={color} roughness={0.8} /></mesh>
      ))}
    </group>
  );
};

const Info = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3 py-3 border-b border-stone-100 last:border-0">
    <span className="w-9 h-9 rounded-xl bg-[#F26B1F]/10 text-[#F26B1F] flex items-center justify-center flex-shrink-0"><Icon size={16} /></span>
    <div className="min-w-0"><p className="text-[11px] text-stone-500">{label}</p><p className="text-sm font-semibold text-stone-900 break-words" data-testid={testid}>{value || '—'}</p></div>
  </div>
);

export const MaterialDetailSheet = ({ material, onClose, inPurchaseList, onTogglePurchase }) => {
  const [view, setView] = useState('3d');
  const cat = material ? categoryOf(material) : 'ovrigt';
  const count = material ? Math.max(4, Math.min(40, Math.round(Math.log10((material.quantity || 1) + 1) * 9))) : 4;
  return (
    <Sheet open={!!material} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto" data-testid="material-detail-sheet">
        {material && (
          <>
            <div className="bg-[#141312] text-white">
              <SheetHeader className="px-5 pt-5"><SheetTitle className="text-white text-lg text-left pr-6">{material.specific_product || material.name}</SheetTitle></SheetHeader>
              <p className="px-5 text-xs text-white/55">{material.phase} · {CATEGORY_LABEL[cat]}</p>
              <div className="h-56 mt-2">
                {view === '3d' ? (
                  <Canvas shadows camera={{ position: [5, 4.5, 6], fov: 40 }} gl={{ antialias: true }}>
                    <color attach="background" args={['#141312']} />
                    <hemisphereLight args={['#fff', '#333', 0.8]} />
                    <directionalLight position={[4, 8, 3]} intensity={1.6} castShadow />
                    <Stack count={count} color={COLORS[cat]} />
                    <OrbitControls enablePan={false} autoRotate autoRotateSpeed={1.2} />
                  </Canvas>
                ) : (
                  <div className="h-full flex items-center justify-center op-grid-bg" data-testid="material-detail-2d">
                    <div className="border-2 border-dashed border-[#F26B1F] rounded-lg px-8 py-6 text-center">
                      <p className="text-3xl font-bold op-num">{fmt(material.quantity, 1)} <span className="text-lg text-white/60">{material.unit}</span></p>
                      <p className="text-[11px] text-white/50 mt-1">{material.specification || 'Total mängd inkl. spill'}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-1.5 pb-4">
                {['3d', '2d'].map((v) => (
                  <button key={v} onClick={() => setView(v)} data-testid={`material-detail-view-${v}`}
                    className={`h-8 px-4 rounded-full text-xs font-bold ${view === v ? 'bg-[#F26B1F] text-white' : 'bg-white/10 text-white/70'}`}>{v.toUpperCase()}</button>
                ))}
              </div>
            </div>
            <div className="p-5">
              <div className="op-card p-4 flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#F26B1F]/10 text-[#F26B1F] flex items-center justify-center"><Ruler size={18} /></span>
                <div><p className="text-xs text-stone-500">Total mängd</p>
                  <p className="text-2xl font-bold op-num" data-testid="material-detail-qty">{fmt(material.quantity, 1)} {material.unit}</p>
                  <p className="text-xs text-stone-500 op-num">Ca. {fmt(material.estimated_total_price)} kr</p></div>
              </div>
              <div className="mt-3">
                <Info icon={Calculator} label="Enhetspris" value={`${fmt(material.estimated_unit_price, 1)} kr/${material.unit}`} testid="material-detail-unit-price" />
                <Info icon={Truck} label="Leverantör (förslag)" value={material.where_to_buy} testid="material-detail-supplier" />
                <Info icon={Percent} label="Spill" value={material.waste_pct ? `${material.waste_pct}% — ${material.waste_reasoning || 'standardspill'}` : null} />
                <Info icon={Calculator} label="Beräkningsgång" value={material.comment} />
                <Info icon={ShieldCheck} label="Säkerhet" value={{ high: 'Hög', medium: 'Medel', low: 'Låg' }[material.confidence] || material.confidence} />
              </div>
              <button onClick={() => onTogglePurchase(material)} data-testid="material-detail-purchase-btn"
                className={`${inPurchaseList ? 'op-btn-ghost' : 'op-btn-primary'} w-full mt-4 h-12`}>
                {inPurchaseList ? <><Check size={16} />I inköpslistan — ta bort</> : <><ShoppingCart size={16} />Lägg till i inköpslista</>}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
